/**
 * Workspace store.
 *
 * Owns the workspace list and is the single orchestration point for
 * switching the active one: `setActive` updates `settingsStore`'s
 * `activeWorkspaceId` (the persisted seam every workspace-scoped store reads)
 * and then reloads the stores that query by `workspace_id` server-side —
 * Notes/Prompts/Projects/Tasks — so the switch is instant. Conversations and
 * Knowledge collections are workspace-*tagged* rather than workspace-*queried*
 * (see `WORKSPACE_SYSTEM.md`): their stores already hold every record, and
 * views filter by `useSettingsStore().activeWorkspaceId` reactively, so they
 * need no explicit reload here.
 */
import { create } from "zustand";
import type { ChatParams, Workspace } from "@/types";
import { DEFAULT_WORKSPACE_ID } from "@/types";
import { workspaceRepository } from "@/services/storage/workspaceRepository";
import { logger } from "@/services/logger";
import { isTauri } from "@/utils";
import { useAppStore } from "./appStore";
import { useSettingsStore } from "./settingsStore";
import { useNotesStore } from "./notesStore";
import { usePromptStore } from "./promptStore";
import { useProjectStore } from "./projectStore";
import { useTaskStore } from "./taskStore";

const log = logger.scope("workspace-store");

interface WorkspaceState {
  workspaces: Workspace[];
  loaded: boolean;

  init: () => Promise<void>;
  refresh: () => Promise<void>;
  create: (name: string) => Promise<void>;
  rename: (id: string, name: string) => Promise<void>;
  setIcon: (id: string, icon: string) => Promise<void>;
  setDefaultModel: (id: string, modelId: string | null) => Promise<void>;
  setAiParams: (id: string, params: Partial<ChatParams>) => Promise<void>;
  delete: (id: string) => Promise<void>;
  /** Switches the active workspace and reloads every scoped store. */
  setActive: (id: string) => Promise<void>;
}

async function guarded(action: () => Promise<void>, failureTitle: string): Promise<void> {
  try {
    await action();
  } catch (err) {
    log.error(failureTitle, err);
    useAppStore.getState().addToast({
      title: failureTitle,
      description: err instanceof Error ? err.message : undefined,
      variant: "error",
    });
  }
}

/** Every store that queries by `workspace_id` server-side, reloaded on
 * switch — a new such store only needs to be added here to join the
 * "instant switch" behaviour. */
function refreshScopedStores(): Promise<unknown> {
  return Promise.all([
    useNotesStore.getState().refresh(),
    usePromptStore.getState().refresh(),
    useProjectStore.getState().refresh(),
    useTaskStore.getState().refresh(),
  ]);
}

export const useWorkspaceStore = create<WorkspaceState>()((set, get) => ({
  workspaces: [{
    id: DEFAULT_WORKSPACE_ID,
    name: "Personal",
    icon: "Layers",
    color: null,
    defaultModel: null,
    aiParams: {},
    isDefault: true,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  }],
  loaded: false,

  init: async () => {
    if (get().loaded || !isTauri()) {
      set({ loaded: true });
      return;
    }
    await get().refresh();
  },

  refresh: async () => {
    try {
      const workspaces = await workspaceRepository.list();
      set({ workspaces: workspaces.length > 0 ? workspaces : get().workspaces, loaded: true });
    } catch (err) {
      log.error("Failed to load workspaces", err);
      set({ loaded: true });
    }
  },

  create: async (name) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    await guarded(async () => {
      await workspaceRepository.create(trimmed);
      await get().refresh();
    }, "Couldn't create workspace");
  },

  rename: async (id, name) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    await guarded(async () => {
      await workspaceRepository.rename(id, trimmed);
      await get().refresh();
    }, "Couldn't rename workspace");
  },

  setIcon: async (id, icon) => {
    await guarded(async () => {
      await workspaceRepository.setIcon(id, icon);
      await get().refresh();
    }, "Couldn't update workspace icon");
  },

  setDefaultModel: async (id, modelId) => {
    await guarded(async () => {
      await workspaceRepository.setDefaultModel(id, modelId);
      await get().refresh();
    }, "Couldn't update workspace default model");
  },

  setAiParams: async (id, params) => {
    await guarded(async () => {
      await workspaceRepository.setAiParams(id, params);
      await get().refresh();
    }, "Couldn't update workspace AI settings");
  },

  delete: async (id) => {
    if (useSettingsStore.getState().activeWorkspaceId === id) {
      await get().setActive(DEFAULT_WORKSPACE_ID);
    }
    await guarded(async () => {
      await workspaceRepository.delete(id);
      await get().refresh();
    }, "Couldn't delete workspace");
  },

  setActive: async (id) => {
    if (useSettingsStore.getState().activeWorkspaceId === id) return;
    useSettingsStore.getState().set("activeWorkspaceId", id);
    await refreshScopedStores();
  },
}));
